import { isEqual } from "lodash";
import {
  SpecialtyGroupExceptionGrade,
  SpecialtyGroupExceptionReviewStatus,
  SpecialtyGroupExceptionStatus
} from "@app/exceptions/enums";
import { SupplementaryDocument } from "@app/exceptions/interfaces";

export const normalizeSpaces = (text: string): string => {
    if (!text) {
      return '';
    }
    return text.replace(/\s+/g, ' ').trim();
  }
;

const handleDateValidation = (startDate: string, endDate: string): boolean => {
  return !endDate || !startDate || startDate < endDate;
}

export const hasChanges = (form: EditExceptionForm): boolean => {
  return !(isEqual(form.comment, form.originalComment)
    && isEqual(form.narrative, form.originalNarrative)
    && isEqual(form.previouslyApproved, form.originalPreviouslyApproved)
    && isEqual(form.excludeFromCCIIOJustification, form.originalExcludeFromCCIIOJustification)
    && isEqual(form.status, form.originalStatus)
    && isEqual(form.uploadedDocuments, form.originalUploadedDocuments)
    && isEqual(form.startDate, form.originalStartDate)
    && isEqual(form.grade, form.originalGrade)
    && isEqual(form.endDate, form.originalEndDate)
    && isEqual(form.reviewStatus, form.originalReviewStatus));
}

export const hasValidFields = (form: EditExceptionForm): boolean => {
  return form.validStartDate && form.validEndDate;
}

export interface EditExceptionForm {
  comment: string;
  endDate: string;
  excludeFromCCIIOJustification: boolean | undefined;
  grade: SpecialtyGroupExceptionGrade;
  narrative: string;
  originalComment: string;
  originalEndDate: string;
  originalExcludeFromCCIIOJustification: boolean | undefined;
  originalGrade: SpecialtyGroupExceptionGrade;
  originalNarrative: string;
  originalPreviouslyApproved: boolean | undefined;
  originalReviewStatus: SpecialtyGroupExceptionReviewStatus;
  originalStartDate: string;
  originalStatus: SpecialtyGroupExceptionStatus;
  originalUploadedDocuments: SupplementaryDocument[];
  previouslyApproved: boolean | undefined;
  reviewStatus: SpecialtyGroupExceptionReviewStatus;
  startDate: string;
  status: SpecialtyGroupExceptionStatus;
  uploadedDocuments: SupplementaryDocument[];
  validEndDate: boolean;
  validStartDate: boolean;
}

export enum EditExceptionFormActionType {
  Narrative = "narrative",
  Comment = "comment",
  PreviouslyApproved = "previouslyApproved",
  Status = "status",
  StartDate = "startDate",
  EndDate = "endDate",
  Grade = "grade",
  AddUploadedDocuments = "addUploadedDocuments",
  RemoveUploadedDocumentByKey = "removeUploadedDocumentByKey",
  Reset = "reset",
  ResetStatus = "resetStatus",
  ReviewStatus = "reviewStatus",
  ExcludeFromCciioJustification = "excludeFromCciioJustification"
}

export type EditExceptionFormAction = { type: EditExceptionFormActionType.Comment, payload: string }
  | { type: EditExceptionFormActionType.Narrative, payload: string }
  | { type: EditExceptionFormActionType.PreviouslyApproved, payload: boolean }
  | { type: EditExceptionFormActionType.ExcludeFromCciioJustification, payload: boolean }
  | { type: EditExceptionFormActionType.Status, payload: SpecialtyGroupExceptionStatus }
  | { type: EditExceptionFormActionType.StartDate, payload: string }
  | { type: EditExceptionFormActionType.EndDate, payload: string }
  | { type: EditExceptionFormActionType.Grade, payload: SpecialtyGroupExceptionGrade }
  | { type: EditExceptionFormActionType.AddUploadedDocuments, payload: SupplementaryDocument[] }
  | { type: EditExceptionFormActionType.RemoveUploadedDocumentByKey, payload: number }
  | { type: EditExceptionFormActionType.ReviewStatus, payload: SpecialtyGroupExceptionReviewStatus }
  | {
  type: EditExceptionFormActionType.Reset, payload: {
    comment: string,
    endDate: string,
    excludeFromCCIIOJustification: boolean | undefined,
    grade: SpecialtyGroupExceptionGrade,
    narrative: string,
    previouslyApproved: boolean | undefined,
    reviewStatus: SpecialtyGroupExceptionReviewStatus,
    startDate: string,
    status: SpecialtyGroupExceptionStatus,
    uploadedDocuments: SupplementaryDocument[]
  }
}
  | {
  type: EditExceptionFormActionType.ResetStatus, payload: SpecialtyGroupExceptionStatus
};

export const editExceptionFormReducer = (state: EditExceptionForm, action: EditExceptionFormAction): EditExceptionForm => {
  switch (action.type) {
    case EditExceptionFormActionType.Comment:
      return {
        ...state,
        comment: action.payload
      };
    case EditExceptionFormActionType.Narrative:
      return {
        ...state,
        narrative: action.payload
      };
    case EditExceptionFormActionType.PreviouslyApproved:
      return {
        ...state,
        previouslyApproved: action.payload
      };
    case EditExceptionFormActionType.ExcludeFromCciioJustification:
      return {
        ...state,
        excludeFromCCIIOJustification: action.payload
      };
    case EditExceptionFormActionType.Status:
      return {
        ...state,
        status: action.payload
      };
    case EditExceptionFormActionType.StartDate:
      return {
        ...state,
        startDate: action.payload,
        validStartDate: handleDateValidation(action.payload, state.endDate),
      };
    case EditExceptionFormActionType.EndDate:
      return {
        ...state,
        endDate: action.payload,
        validEndDate: handleDateValidation(state.startDate, action.payload),
      };
    case EditExceptionFormActionType.Grade:
      return {
        ...state,
        grade: action.payload
      };
    case EditExceptionFormActionType.AddUploadedDocuments:
      return {
        ...state,
        uploadedDocuments: [...state.uploadedDocuments, ...action.payload]
      };
    case EditExceptionFormActionType.RemoveUploadedDocumentByKey:
      return {
        ...state,
        uploadedDocuments: state.uploadedDocuments.filter(item => item.supplementaryDocumentMetadataId != action.payload)
      }
    case EditExceptionFormActionType.ReviewStatus:
      return {
        ...state,
        reviewStatus: action.payload
      };
    case EditExceptionFormActionType.Reset:
      return {
        ...state,
        comment: normalizeSpaces(action.payload.comment),
        endDate: action.payload.endDate,
        excludeFromCCIIOJustification: action.payload.excludeFromCCIIOJustification,
        grade: action.payload.grade,
        narrative: normalizeSpaces(action.payload.narrative),
        originalComment: normalizeSpaces(action.payload.comment),
        originalEndDate: action.payload.endDate,
        originalExcludeFromCCIIOJustification: action.payload.excludeFromCCIIOJustification,
        originalGrade: action.payload.grade,
        originalNarrative: normalizeSpaces(action.payload.narrative),
        originalPreviouslyApproved: action.payload.previouslyApproved,
        originalReviewStatus: action.payload.reviewStatus,
        originalStartDate: action.payload.startDate,
        originalStatus: action.payload.status,
        originalUploadedDocuments: [...state.uploadedDocuments, ...action.payload.uploadedDocuments],
        previouslyApproved: action.payload.previouslyApproved,
        reviewStatus: action.payload.reviewStatus,
        startDate: action.payload.startDate,
        status: action.payload.status,
        uploadedDocuments: [...state.uploadedDocuments, ...action.payload.uploadedDocuments],
        validEndDate: true,
        validStartDate: true,
      }
    case EditExceptionFormActionType.ResetStatus:
      return {
        ...state,
        originalStatus: action.payload,
        status: action.payload
      }
    default:
      return state;
  }
}
